import React, { useState, useEffect } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  SortingState,
  getSortedRowModel,
  VisibilityState,
  getPaginationRowModel,
  OnChangeFn,
  ColumnFiltersState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { VoucherResponse } from '@/app/types/voucher';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';

// Define a type for the custom meta properties
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

// Use intersection type for ExtendedColumnDef
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};

interface DataTableProps<TData, TValue> {
  columns: ExtendedColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  pageCount: number;
  page: number;
  setPage: (page: number) => void;
  onSortingChange?: (sorting: SortingState) => void;
  sortingState?: SortingState;
  onFilterChange?: (filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }>) => void;
  initialFilters?: Record<string, string | number | boolean | string[] | { from?: string; to?: string } | null>;
}

export function PurchaseInvoiceDataTable<TData extends VoucherResponse, TValue>({
  columns,
  data,
  isLoading = false,
  pageCount,
  page,
  setPage,
  onSortingChange,
  sortingState = [],
  onFilterChange,
  initialFilters,
}: DataTableProps<TData, TValue>): JSX.Element {
  const [sorting, setSorting] = useState<SortingState>(sortingState);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  
  // Initialize column filters from initialFilters prop
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
    if (!initialFilters) return [];
    
    const filters: ColumnFiltersState = [];
    Object.entries(initialFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Handle arrays (empty arrays are valid)
        if (Array.isArray(value) || value !== '') {
          filters.push({ id: key, value });
        }
      }
    });
    return filters;
  });
  
  const navigate = useNavigate();
  
  // Sync initial filters on first mount only
  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (!hasInitialized && initialFilters) {
      const filters: ColumnFiltersState = [];
      Object.entries(initialFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Handle arrays (empty arrays are valid)
          if (Array.isArray(value) || value !== '') {
            filters.push({ id: key, value });
          }
        }
      });
      setColumnFilters(filters);
      setHasInitialized(true);
    }
  }, [hasInitialized, initialFilters]);

  // Create a stable debounced filter change handler
  const debouncedFilterChange = React.useMemo(
    () => debounce((filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }>) => {
      console.log('Debounced filter change - calling onFilterChange with:', filters);
      onFilterChange?.(filters);
    }, 300),
    [onFilterChange]
  );

  // Use useEffect to send filter changes to parent component
  useEffect(() => {
    // Skip the initial render to prevent clearing URL filters
    if (!hasInitialized) {
      return;
    }
    
    if (onFilterChange) {
      // Convert column filters to a format that can be used by the API
      const apiFilters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }> = {};
      
      console.log('Column filters changed:', columnFilters);
      
      columnFilters.forEach((filter) => {
        const value = filter.value;
        if (value !== undefined && value !== null) {
          // Handle arrays (for multi-select) - check length instead of empty string
          if (Array.isArray(value)) {
            if (value.length > 0) {
              apiFilters[filter.id] = value;
            }
          }
          // Handle date range objects
          else if (typeof value === 'object' && 'from' in value) {
            const dateRange = value as { from?: string; to?: string };
            // Only add if at least one date is set
            if (dateRange.from || dateRange.to) {
              apiFilters[filter.id] = dateRange;
            }
          }
          // Handle simple types
          else if (value !== '' && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
            apiFilters[filter.id] = value;
          }
        }
      });
      
      console.log('Column filters changed:', columnFilters);
      console.log('API filters to send:', apiFilters);
      debouncedFilterChange(apiFilters);
    }
    
    // Cleanup function
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [columnFilters, debouncedFilterChange, onFilterChange, hasInitialized]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = updater instanceof Function ? updater(sorting) : updater;
    setSorting(newSorting);
    if (onSortingChange) {
      onSortingChange(newSorting);
    }
    setPage(0);
  };

  const handleColumnFiltersChange = (updater: ColumnFiltersState | ((state: ColumnFiltersState) => ColumnFiltersState)): void => {
    const newFilters = updater instanceof Function ? updater(columnFilters) : updater;
    setColumnFilters(newFilters);
    // Reset to first page when filters change
    setPage(0);
  };

  const table = useReactTable({
    data,
    columns: columns as ColumnDef<TData, TValue>[], // Cast back for useReactTable
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: handleSortingChange,
    getSortedRowModel: getSortedRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: handleColumnFiltersChange,
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
    manualFiltering: true, 
    pageCount,
    state: {
      sorting,
      columnVisibility,
      columnFilters,
      pagination: {
        pageIndex: page,
        pageSize: 10,
      },
    },
  });

  const handleRowClick = (e: React.MouseEvent, id: number): void => {
    const path = `/purchase-invoices/detail/${id}`;
    
    // Check if Ctrl/Cmd is pressed to open in new tab
    if ((e.ctrlKey || e.metaKey) && window.api?.tabs) {
      e.preventDefault();
      window.api.tabs.create(path, `Invoice #${id}`);
    } else {
      navigate(path);
    }
  };

  const renderPaginationControls = (): JSX.Element => (
    <div className="flex items-center justify-between w-full">
      <div className="text-sm text-muted-foreground">
        Page {page + 1} of {pageCount || 1}
      </div>
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page - 1)}
          disabled={page === 0 || isLoading}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(page + 1)}
          disabled={page + 1 >= pageCount || isLoading}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
  
  const getColumnKey = (column: ExtendedColumnDef<TData, TValue>): string => {
    return column.id ?? (column as { accessorKey?: string }).accessorKey ?? '';
  };
  
  const renderFilterControl = (column: ExtendedColumnDef<TData, TValue>): JSX.Element | null => {
    const columnKey = getColumnKey(column);
    const tableColumn = table.getColumn(columnKey);
    const columnMeta = column.meta; 
    
    if (!tableColumn || !column.enableColumnFilter || !columnMeta?.variant) {
      return null;
    }

    const filterValue = tableColumn.getFilterValue();

    if (columnMeta.variant === 'text' || columnMeta.variant === 'number') {
      return (
        <Input
          type={columnMeta.variant === 'number' ? 'number' : 'text'}
          value={(filterValue ?? '') as string}
          onChange={(e) => tableColumn.setFilterValue(e.target.value)}
          placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`}
          className="h-8 w-full max-w-xs"
        />
      );
    }

    if (columnMeta.variant === 'select' && columnMeta.options) {
      const ALL_VALUE = "__ALL__"; // Special value for the "All" option
      return (
        <Select 
          value={(filterValue ?? ALL_VALUE) as string} // Default to ALL_VALUE if filter is undefined
          onValueChange={value => 
            tableColumn.setFilterValue(value === ALL_VALUE ? undefined : value)
          }
        >
          <SelectTrigger className="h-8 w-full max-w-xs">
            <SelectValue placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`} />
          </SelectTrigger>
          <SelectContent>
            {/* Use the special value for the "All" item */}
            <SelectItem value={ALL_VALUE}>All</SelectItem> 
            {columnMeta.options.map((option) => (
              <SelectItem key={String(option.value)} value={String(option.value)}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    
    if (columnMeta.variant === 'date') {
       // Render a disabled input as a placeholder for date filter UI
       return <Input placeholder={columnMeta.placeholder || `Filter by ${columnMeta.label}...`} className="h-8 w-full max-w-xs" disabled />
    }

    if (columnMeta.variant === 'dateRange') {
      // Get the filter value which should be an object with from/to string dates
      const filterVal = filterValue as { from?: string; to?: string } | undefined;
      
      // Convert string dates to Date objects for the DateRangePicker
      const dateRange: DateRange | undefined = filterVal ? {
        from: filterVal.from ? new Date(filterVal.from) : undefined,
        to: filterVal.to ? new Date(filterVal.to) : undefined,
      } : undefined;
      
      return (
        <DateRangePicker
          date={dateRange}
          onDateChange={(newDate) => {
            if (newDate?.from || newDate?.to) {
              // Format dates as ISO strings for the filter
              const newFilterVal = {
                from: newDate.from ? format(newDate.from, 'yyyy-MM-dd') : undefined,
                to: newDate.to ? format(newDate.to, 'yyyy-MM-dd') : undefined,
              };
              tableColumn.setFilterValue(newFilterVal);
            } else {
              tableColumn.setFilterValue(undefined);
            }
          }}
          className="h-8 w-[300px]"
        />
      );
    }

    if (columnMeta.variant === 'multiSelect' && columnMeta.options) {
      const selectedValues = (filterValue as (string | number)[]) || [];
      
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="h-8 w-full max-w-xs justify-between">
              {selectedValues.length > 0 ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs">{selectedValues.length} selected</span>
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">
                  {columnMeta.placeholder || `Filter by ${columnMeta.label}...`}
                </span>
              )}
              <ChevronDown className="h-4 w-4 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[200px] p-0" align="start">
            <div className="p-2">
              <div className="space-y-1">
                {columnMeta.options.map((option) => (
                  <div key={String(option.value)} className="flex items-center space-x-2 py-1">
                    <Checkbox
                      id={`${columnKey}-${option.value}`}
                      checked={selectedValues.some(v => String(v) === String(option.value))}
                      onCheckedChange={(checked) => {
                        const newValues = checked
                          ? [...selectedValues, option.value]
                          : selectedValues.filter(v => String(v) !== String(option.value));
                        console.log('Multi-select filter change:', columnKey, newValues);
                        console.log('Setting filter value to:', newValues.length > 0 ? newValues : undefined);
                        tableColumn.setFilterValue(newValues.length > 0 ? newValues : undefined);
                      }}
                    />
                    <label
                      htmlFor={`${columnKey}-${option.value}`}
                      className="text-sm font-normal cursor-pointer flex-1"
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
              {selectedValues.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => tableColumn.setFilterValue(undefined)}
                >
                  Clear
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      );
    }

    return null;
  };

  return (
    <div className="space-y-4">
      {/* Column Visibility Controls */} 
      <div className="flex items-center justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-9">
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {columns.map((column) => {
              const columnKey = getColumnKey(column);
              const tableCol = table.getColumn(columnKey);
              // Ensure tableCol exists and getCanHide is callable
              return tableCol?.getCanHide() && (
                <DropdownMenuCheckboxItem
                  key={columnKey}
                  className="capitalize"
                  checked={tableCol.getIsVisible()} 
                  onCheckedChange={(value) =>
                    tableCol.toggleVisibility(!!value) 
                  }
                >
                  {column.meta?.label || columnKey} 
                </DropdownMenuCheckboxItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Row for Individual Column Filters */} 
      <div className="flex items-center gap-2 flex-wrap p-2 border rounded-md bg-card">
        {columns.map((column) => (
            column.enableColumnFilter && (
                <div key={getColumnKey(column)} className="flex flex-col space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">
                        {column.meta?.label || getColumnKey(column)} 
                    </label>
                    {renderFilterControl(column)}
                </div>
            )
        ))}
      </div>
      
      {/* Data Table */} 
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    Loading...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow 
                  key={row.id} 
                  data-state={row.getIsSelected() && "selected"}
                  className="hover:bg-muted"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination controls */}
      {renderPaginationControls()}
    </div>
  );
}